// src/screens/ShippingScreen.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { saveShippingAddress } from '../redux/features/Cart/cartSlice';
import { getAddresses, addAddress, deleteAddress, setDefaultAddress } from '../services/addressServices';
import CheckoutSteps from '../components/CheckoutSteps';
import Loader from '../components/Loader';
import Message from '../components/Message';
import { MapPin, Plus, Trash2, Edit2, CheckCircle, X, Loader2, ChevronLeft, Phone, Star } from 'lucide-react';

const IRAN_PROVINCES = [
  'آذربایجان شرقی', 'آذربایجان غربی', 'اردبیل', 'اصفهان', 'البرز',
  'ایلام', 'بوشهر', 'تهران', 'چهارمحال و بختیاری', 'خراسان جنوبی',
  'خراسان رضوی', 'خراسان شمالی', 'خوزستان', 'زنجان', 'سمنان',
  'سیستان و بلوچستان', 'فارس', 'قزوین', 'قم', 'کردستان',
  'کرمان', 'کرمانشاه', 'کهگیلویه و بویراحمد', 'گلستان', 'گیلان',
  'لرستان', 'مازندران', 'مرکزی', 'هرمزگان', 'همدان', 'یزد'
];

const COLORS = {
  primary: '#8A9A7B',
  primaryLight: '#9EAD8C',
  primaryDark: '#6A7A5B',
  primaryBg: '#F0F4EB',
  border: '#DCE4D3',
  danger: '#D4837A',
  success: '#7A9B8A',
};

const FormInput = ({ label, name, value, onChange, type = 'text', placeholder, required = false, error = '', icon: Icon, ...props }) => {
  const inputClasses = `w-full rounded-xl border-2 px-4 py-3 text-sm outline-none transition-all duration-200 bg-white ${
    error ? 'border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-500/10' : 'border-gray-200 focus:border-[#8A9A7B] focus:ring-4 focus:ring-[#8A9A7B]/10'
  }`;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={name} className="text-sm font-bold text-gray-700">{label}{required && <span className="text-red-400 mr-0.5">*</span>}</label>
      <div className="relative">
        {Icon && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><Icon className="w-5 h-5" /></div>}
        <input id={name} name={name} type={type} value={value} onChange={onChange} placeholder={placeholder} className={`${inputClasses} ${Icon ? 'pr-11' : ''}`} {...props} />
      </div>
      {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
    </div>
  );
};

const ProvinceSelect = ({ value, onChange, error }) => {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor="province" className="text-sm font-bold text-gray-700">استان <span className="text-red-400 mr-0.5">*</span></label>
      <select id="province" value={value} onChange={onChange} className={`w-full rounded-xl border-2 px-4 py-3 text-sm outline-none transition-all duration-200 bg-white appearance-none cursor-pointer ${
        error ? 'border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-500/10' : 'border-gray-200 focus:border-[#8A9A7B] focus:ring-4 focus:ring-[#8A9A7B]/10'
      }`}>
        <option value="">انتخاب استان...</option>
        {IRAN_PROVINCES.map(prov => <option key={prov} value={prov}>{prov}</option>)}
      </select>
      {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
    </div>
  );
};

const AddressCard = ({ address, isSelected, onSelect, onSetDefault, onDelete, isDeleting }) => {
  const formatPhone = (phone) => {
    if (!phone) return null;
    if (phone.length === 11) {
      return `۰${phone.slice(1, 3)} ${phone.slice(3, 6)} ${phone.slice(6, 9)} ${phone.slice(9)}`;
    }
    return phone;
  };

  return (
    <div className={`relative rounded-2xl border-2 p-4 transition-all duration-300 cursor-pointer ${
      isSelected ? 'border-[#8A9A7B] bg-[#F0F4EB] shadow-lg shadow-[#8A9A7B]/20' : 'border-gray-200 bg-white hover:border-[#9EAD8C] hover:shadow-md'
    }`} onClick={onSelect}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'border-[#8A9A7B] bg-[#8A9A7B]' : 'border-gray-300 bg-white'}`}>
            {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <p className="text-sm font-bold text-gray-800 line-clamp-1">{address.address}</p>
            {address.is_default && (
              <span className="inline-flex items-center gap-1 text-xs font-medium bg-[#7A9B8A] text-white px-2.5 py-0.5 rounded-full">
                <Star className="w-3 h-3 fill-current" /> پیش‌فرض
              </span>
            )}
          </div>
          <div className="space-y-0.5 text-xs text-gray-500">
            <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-gray-400" /> {address.city}، {address.province}</p>
            {address.plaque && (
              <p className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 text-gray-400">•</span> پلاک: {address.plaque}{address.unit && ` - واحد: ${address.unit}`}</p>
            )}
            {address.postal_code && <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-gray-400" /> کد پستی: {address.postal_code}</p>}
            {address.phone && <p className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-gray-400" /> {formatPhone(address.phone)}</p>}
          </div>
        </div>
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          {!address.is_default && (
            <button onClick={(e) => { e.stopPropagation(); onSetDefault(address.id); }} className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-[#8A9A7B] bg-[#F0F4EB] rounded-lg hover:bg-[#9EAD8C] hover:text-white transition-all duration-200">
              <Star className="w-3.5 h-3.5" /> پیش‌فرض
            </button>
          )}
          <button onClick={(e) => { e.stopPropagation(); onDelete(address.id); }} disabled={address.is_default || isDeleting} className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 ${address.is_default || isDeleting ? 'bg-gray-100 text-gray-300 cursor-not-allowed' : 'bg-red-50 text-red-500 hover:bg-red-100'}`}>
            {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} حذف
          </button>
        </div>
      </div>
    </div>
  );
};

function ShippingScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ✅ تغییر از userLogin به auth
  const { userInfo } = useSelector((state) => state.auth) || {};
  const { shippingAddress } = useSelector((state) => state.cart) || {};

  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newAddress, setNewAddress] = useState({ address: '', city: '', province: '', postal_code: '', phone: '', plaque: '', unit: '' });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validatePhone = (phone) => /^[0-9]{11}$/.test(phone);

  const fetchAddresses = async () => {
    if (!userInfo?.access) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const res = await getAddresses({ userInfo, dispatch });
      setAddresses(res.data);
      const defaultAddr = res.data.find(addr => addr.is_default);
      if (defaultAddr) {
        setSelectedAddressId(defaultAddr.id);
        dispatch(saveShippingAddress({
          address: defaultAddr.address,
          city: defaultAddr.city,
          postalCode: defaultAddr.postal_code,
          country: defaultAddr.province,
          province: defaultAddr.province,
          phone: defaultAddr.phone,
          plaque: defaultAddr.plaque,
          unit: defaultAddr.unit,
          id: defaultAddr.id,
          is_default: true
        }));
      } else if (res.data.length > 0) {
        setSelectedAddressId(res.data[0].id);
        const first = res.data[0];
        dispatch(saveShippingAddress({
          address: first.address,
          city: first.city,
          postalCode: first.postal_code,
          country: first.province,
          province: first.province,
          phone: first.phone,
          plaque: first.plaque,
          unit: first.unit,
          id: first.id,
          is_default: false
        }));
      }
    } catch (err) {
      setError('خطا در دریافت آدرس‌ها');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userInfo?.access) {
      fetchAddresses();
    } else {
      navigate('/phone?redirect=/shipping');
    }
  }, [userInfo, navigate]);

  const handleSelectAddress = (addressId) => {
    setSelectedAddressId(addressId);
    const selected = addresses.find(addr => addr.id === addressId);
    if (selected) {
      dispatch(saveShippingAddress({
        address: selected.address,
        city: selected.city,
        postalCode: selected.postal_code,
        country: selected.province,
        province: selected.province,
        phone: selected.phone,
        plaque: selected.plaque,
        unit: selected.unit,
        id: selected.id,
        is_default: selected.is_default
      }));
    }
  };

  const submitHandler = (e) => {
    e.preventDefault();
    if (selectedAddressId) {
      navigate('/payment');
    } else {
      alert('لطفاً یک آدرس را انتخاب کنید یا آدرس جدید اضافه کنید.');
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!newAddress.address.trim()) errors.address = 'آدرس الزامی است';
    if (!newAddress.city.trim()) errors.city = 'شهر الزامی است';
    if (!newAddress.province) errors.province = 'استان الزامی است';
    if (newAddress.phone && !validatePhone(newAddress.phone)) {
      errors.phone = 'شماره تلفن باید ۱۱ رقم باشد';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddAddress = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const res = await addAddress(newAddress, { userInfo, dispatch });
      const updatedAddresses = [...addresses, res.data];
      setAddresses(updatedAddresses);
      setSelectedAddressId(res.data.id);
      dispatch(saveShippingAddress({
        address: res.data.address,
        city: res.data.city,
        postalCode: res.data.postal_code,
        country: res.data.province,
        province: res.data.province,
        phone: res.data.phone,
        plaque: res.data.plaque,
        unit: res.data.unit,
        id: res.data.id,
        is_default: res.data.is_default
      }));
      setShowAddForm(false);
      setNewAddress({ address: '', city: '', province: '', postal_code: '', phone: '', plaque: '', unit: '' });
      setFormErrors({});
    } catch (err) {
      alert('خطا در ثبت آدرس: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    const address = addresses.find(addr => addr.id === addressId);
    if (address?.is_default) {
      alert('آدرس پیش‌فرض قابل حذف نیست.');
      return;
    }
    if (!window.confirm('آیا از حذف این آدرس مطمئن هستید؟')) return;
    setDeletingId(addressId);
    try {
      await deleteAddress(addressId, { userInfo, dispatch });
      const updated = addresses.filter(addr => addr.id !== addressId);
      setAddresses(updated);
      if (selectedAddressId === addressId) {
        if (updated.length > 0) {
          setSelectedAddressId(updated[0].id);
          const first = updated[0];
          dispatch(saveShippingAddress({
            address: first.address,
            city: first.city,
            postalCode: first.postal_code,
            country: first.province,
            province: first.province,
            phone: first.phone,
            plaque: first.plaque,
            unit: first.unit,
            id: first.id,
            is_default: first.is_default
          }));
        } else {
          setSelectedAddressId(null);
        }
      }
    } catch (err) {
      alert('خطا در حذف آدرس: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingId(null);
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      await setDefaultAddress(addressId, { userInfo, dispatch });
      const updated = addresses.map(addr => ({ ...addr, is_default: addr.id === addressId }));
      setAddresses(updated);
      setSelectedAddressId(addressId);
      const selected = updated.find(addr => addr.id === addressId);
      if (selected) {
        dispatch(saveShippingAddress({
          address: selected.address,
          city: selected.city,
          postalCode: selected.postal_code,
          country: selected.province,
          province: selected.province,
          phone: selected.phone,
          plaque: selected.plaque,
          unit: selected.unit,
          id: selected.id,
          is_default: true
        }));
      }
    } catch (err) {
      alert('خطا در تنظیم آدرس پیش‌فرض: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleFormChange = (field, value) => {
    setNewAddress(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: '' }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4EB] py-8 px-4" dir="rtl">
        <div className="max-w-4xl mx-auto"><CheckoutSteps step1 step2 /><div className="mt-8 bg-white rounded-2xl shadow-xl p-8 text-center"><Loader /><p className="text-gray-500 mt-4">در حال بارگذاری آدرس‌ها...</p></div></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F0F4EB] py-8 px-4" dir="rtl">
        <div className="max-w-4xl mx-auto"><CheckoutSteps step1 step2 /><div className="mt-8 bg-white rounded-2xl shadow-xl p-8"><Message variant="danger" text={error} /><button onClick={fetchAddresses} className="mt-4 px-6 py-2 bg-[#8A9A7B] text-white rounded-xl hover:bg-[#6A7A5B] transition-colors">تلاش مجدد</button></div></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4EB] py-8 px-4" dir="rtl">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6"><CheckoutSteps step1 step2 /></div>
        <div className="mt-6"><h1 className="text-2xl md:text-3xl font-bold text-[#3D4A33] flex items-center gap-3"><MapPin className="w-8 h-8 text-[#8A9A7B]" /> آدرس ارسال</h1><p className="text-gray-500 text-sm mt-1">آدرس مورد نظر خود را انتخاب کنید یا آدرس جدید اضافه کنید.</p></div>

        <div className="mt-6 bg-white rounded-2xl shadow-xl p-4 md:p-6">
          {addresses.length > 0 ? (
            <div className="space-y-3">
              {addresses.map((address) => (
                <AddressCard
                  key={address.id}
                  address={address}
                  isSelected={selectedAddressId === address.id}
                  onSelect={() => handleSelectAddress(address.id)}
                  onSetDefault={handleSetDefault}
                  onDelete={handleDeleteAddress}
                  isDeleting={deletingId === address.id}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8"><MapPin className="w-16 h-16 text-gray-300 mx-auto mb-3" /><p className="text-gray-500 font-medium">هیچ آدرسی ثبت نشده است</p><p className="text-sm text-gray-400 mt-1">برای ادامه، آدرس جدید اضافه کنید.</p></div>
          )}
        </div>

        <div className="mt-4">
          <button onClick={() => setShowAddForm(!showAddForm)} className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all duration-300 ${showAddForm ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-[#8A9A7B] text-white hover:bg-[#6A7A5B] shadow-md hover:shadow-lg'}`}>
            {showAddForm ? <><X className="w-5 h-5" /> بستن فرم</> : <><Plus className="w-5 h-5" /> افزودن آدرس جدید</>}
          </button>
        </div>

        {showAddForm && (
          <div className="mt-4 bg-white rounded-2xl shadow-xl p-4 md:p-6 border-2 border-[#8A9A7B]/20">
            <h3 className="text-lg font-bold text-[#3D4A33] mb-4 flex items-center gap-2"><Plus className="w-5 h-5 text-[#8A9A7B]" /> ثبت آدرس جدید</h3>
            <div className="space-y-4">
              <FormInput label="آدرس" name="address" value={newAddress.address} onChange={(e) => handleFormChange('address', e.target.value)} placeholder="خیابان، محله، کوچه، پلاک" required error={formErrors.address} icon={MapPin} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ProvinceSelect value={newAddress.province} onChange={(e) => handleFormChange('province', e.target.value)} error={formErrors.province} />
                <FormInput label="شهر" name="city" value={newAddress.city} onChange={(e) => handleFormChange('city', e.target.value)} placeholder="مثال: تهران" required error={formErrors.city} icon={MapPin} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormInput label="کد پستی" name="postal_code" value={newAddress.postal_code} onChange={(e) => handleFormChange('postal_code', e.target.value)} placeholder="۱۰ رقمی" icon={MapPin} />
                <FormInput label="پلاک" name="plaque" value={newAddress.plaque} onChange={(e) => handleFormChange('plaque', e.target.value)} placeholder="مثال: ۱۲۳" icon={MapPin} />
                <FormInput label="واحد" name="unit" value={newAddress.unit} onChange={(e) => handleFormChange('unit', e.target.value)} placeholder="مثال: ۵" icon={MapPin} />
              </div>
              <FormInput label="شماره تلفن همراه" name="phone" type="tel" value={newAddress.phone} onChange={(e) => { const value = e.target.value.replace(/\D/g, ''); if (value.length <= 11) handleFormChange('phone', value); }} placeholder="۰۹۱۲۳۴۵۶۷۸۹" maxLength={11} error={formErrors.phone} icon={Phone} />
              <button onClick={handleAddAddress} disabled={submitting} className="w-full py-3.5 bg-[#8A9A7B] text-white rounded-xl font-bold hover:bg-[#6A7A5B] transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> در حال ثبت...</> : 'ثبت آدرس'}
              </button>
            </div>
          </div>
        )}

        <div className="mt-6">
          <button onClick={submitHandler} disabled={!selectedAddressId} className={`w-full py-4 rounded-2xl font-bold text-white text-lg transition-all duration-300 flex items-center justify-center gap-2 ${selectedAddressId ? 'bg-[#8A9A7B] hover:bg-[#6A7A5B] shadow-lg hover:shadow-xl active:scale-[0.98]' : 'bg-gray-300 cursor-not-allowed'}`}>
            ادامه به مرحله پرداخت <ChevronLeft className="w-5 h-5" />
          </button>
          {!selectedAddressId && addresses.length > 0 && <p className="text-center text-sm text-gray-400 mt-2">لطفاً یک آدرس را انتخاب کنید</p>}
        </div>
      </div>
    </div>
  );
}

export default ShippingScreen;